// Page errors: uncaught errors and unhandled promise rejections are remembered for the β sheet (lib/trail.ts) and sent
// to POST /api/client-errors in small batches, so triage sees crashes nobody reported. console.error is remembered
// only. Noise (ResizeObserver, cross-origin "Script error.", aborted requests, extensions) is dropped.
import { api } from '../api/client';
import { crumb, rememberError } from '../lib/trail';
import { routerStore } from '../router';
import { platform } from '../native';
import { BUILD } from './build';

const NOISE = /ResizeObserver loop|^Script error\.?$|AbortError|The user aborted|Load failed$|chrome-extension:|moz-extension:|safari-extension:/i;
const MAX_PER_SESSION = 20;
let sent = 0;
let queue: { message: string; stack?: string; screen?: string; at: string }[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
const seen = new Set<string>();

function describe(x: unknown): { message: string; stack?: string } {
  if (x instanceof Error) return { message: `${x.name && x.name !== 'Error' ? x.name + ': ' : ''}${x.message}`, stack: x.stack };
  if (typeof x === 'string') return { message: x };
  try { return { message: JSON.stringify(x).slice(0, 500) }; } catch { return { message: String(x) }; }
}

function flush() {
  timer = null;
  const errors = queue.splice(0, 10);
  if (!errors.length) return;
  sent += errors.length;
  api.clientErrors({ client: platform(), build: BUILD, errors }).catch(() => { /* offline: the β trail still has them */ });
  if (queue.length) timer = setTimeout(flush, 3000);
}

/** Remember an error for the β sheet and (once per message per session) report it. */
export function reportError(x: unknown, where = ''): void {
  const { message, stack } = describe(x);
  if (!message || NOISE.test(message) || (stack && NOISE.test(stack))) return;
  const screen = routerStore.get().s;
  rememberError({ message: where ? `${where}: ${message}` : message, stack, screen });
  crumb(`error ${message.slice(0, 80)}`);
  const key = message.slice(0, 200);
  if (seen.has(key) || sent + queue.length >= MAX_PER_SESSION) return;
  seen.add(key);
  queue.push({ message: where ? `${where}: ${message}` : message, ...(stack ? { stack } : {}), screen, at: new Date().toISOString() });
  if (!timer) timer = setTimeout(flush, 3000);
}

let installed = false;
export function installErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => {
    // Resource load errors (img/video/script) arrive here too, without an Error object: keep them in the trail only.
    if (!(e instanceof ErrorEvent)) return;
    reportError(e.error || e.message || 'Unknown error', e.error ? '' : `${e.filename || ''}:${e.lineno || 0}`);
  });
  window.addEventListener('unhandledrejection', (e) => reportError(e.reason ?? 'Unhandled rejection', 'promise'));
  const orig = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      const first = args.find((a) => a instanceof Error) ?? args.map((a) => (typeof a === 'string' ? a : describe(a).message)).join(' ');
      const { message, stack } = describe(first);
      if (message && !NOISE.test(message)) rememberError({ message: `console: ${message}`, stack, screen: routerStore.get().s });
    } catch { /* never break logging */ }
    orig(...args);
  };
}
